import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { copyTasksToCustomerAssurance } from "@/services/frameworkService";
import { useTenancy } from "@/contexts/TenancyContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

interface ISO45001State {
  enabled: "enabled" | "disabled";
}

const defaultISO45001: ISO45001State = {
  enabled: "disabled",
};

export default function ISO45001Module() {
  const { activeTenancy } = useTenancy();
  const [iso45001, setIso45001] = useState<ISO45001State>(defaultISO45001);
  const [iso45001Initial, setIso45001Initial] = useState<ISO45001State | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchISO45001 = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const iso45001Settings = settings?.["iso45001"] ?? {};
        // Precedence: 'Enabled' (case-insensitive), then 'active', 'enabled', 'Active', etc.
        let isEnabled = false;
        // Helper to check both string and boolean
        function checkEnabled(val: unknown): boolean {
          if (typeof val === 'boolean') return val;
          if (typeof val === 'string') return val.toLowerCase() === 'true' || val.toLowerCase() === 'enabled';
          return false;
        }
        if (typeof iso45001Settings.Enabled !== 'undefined') {
          isEnabled = checkEnabled(iso45001Settings.Enabled);
        } else if (typeof iso45001Settings.enabled !== 'undefined') {
          isEnabled = checkEnabled(iso45001Settings.enabled);
        } else if (typeof iso45001Settings.active !== 'undefined') {
          isEnabled = checkEnabled(iso45001Settings.active);
        } else if (typeof iso45001Settings.Active !== 'undefined') {
          isEnabled = checkEnabled(iso45001Settings.Active);
        }
        setIso45001({ enabled: isEnabled ? "enabled" : "disabled" });
        setIso45001Initial({ enabled: isEnabled ? "enabled" : "disabled" });
      } catch (err) {
        toast.error("Failed to fetch ISO 45001 settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchISO45001();
  }, []);

  const iso45001Changed = iso45001Initial && JSON.stringify(iso45001) !== JSON.stringify(iso45001Initial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const wasDisabled = iso45001Initial?.enabled === "disabled";
      const isNowEnabled = iso45001.enabled === "enabled";
      
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        iso45001: { Enabled: iso45001.enabled === "enabled" },
      });
      
      // DED-1511: If ISO 45001 is being enabled (was disabled, now enabled), copy tasks
      if (wasDisabled && isNowEnabled && activeTenancy?.id) {
        try {
          const customerId = activeTenancy.id;
          
          toast.info("Copying ISO 45001 tasks...");
          
          await copyTasksToCustomerAssurance(
            customerId,
            'ISO45001',
            undefined,
            undefined
          );
          
          toast.success("ISO 45001 settings saved and tasks copied");
        } catch (taskError) {
          console.error('Error copying ISO 45001 tasks:', taskError);
          toast.warning("Settings saved, but there was an error copying some tasks. Please check the console.");
        }
      } else {
        toast.success("ISO 45001 settings saved");
      }
      
      setIso45001Initial({ ...iso45001 });
    } catch (err) {
      toast.error("Failed to save ISO 45001 settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-green-500/10 p-3 rounded-full">
          <span role="img" aria-label="ISO 45001">🦺</span>
        </div>
        <div>
          <div className="font-semibold text-lg">ISO 45001</div>
          <div className="text-xs text-muted-foreground">Occupational Health and Safety Management System Module</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Module Status</label>
        <Select
          value={iso45001.enabled}
          onValueChange={val => setIso45001(v => ({ ...v, enabled: val as ISO45001State["enabled"] }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!iso45001Changed || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save ISO 45001 Settings"}
        </Button>
      </div>
    </div>
  );
}
